import { cloneDeep } from 'lodash';

export default {
    // Default state can be overriden/supplied by the backend via VuexService class.
    state: {
        apps: [],
        deactivatedApp: null,
    },

    mutations: {
        updateApp(state, app) {
            let appIndex = state.apps.findIndex(a => a.name == app.name);
            state.apps[appIndex] = {...app};
        },

        removeApp(state, app) {
            state.apps = state.apps.filter(a => a.name != app.name);
        },

        ON_DEACTIVATE_APP (state, payload) {
            state.deactivatedApp = payload;
        }
    },

    actions: {
        onDeactivateApp ({commit}, appId) {
            commit('ON_DEACTIVATE_APP', appId);
        }
    },

    getters: {
        translatedApps(state) {
            return cloneDeep(state.apps).map(item => {
                if (item.label_key) {
                    item.label = this.trans(item.label_key);
                }
                return item;
            });
        },

        findAppByName: (state) => (appName) => {
            if (state.apps && state.apps.length > 0) {
                let app = state.apps.find(app => app.name == appName);

                if (!app) {
                    console.error(`[apps/findAppByName] App "${appName}" not found. Make sure you spelt it correctly.`);
                }

                return app;
            }

            return null;
        },

        isAppAvailable: (state, getters) => (appName) => {
            let app = getters.findAppByName(appName);

            return app && app.available;
        },

        isAppEnabled: (state, getters) => (appName) => {
            let app = getters.findAppByName(appName);

            return app && app.enabled;
        },

        canEditAppSettings: (state, getters) => (appName) => {
            let app = getters.findAppByName(appName);

            return app && Boolean(app.can_edit_settings || false);
        },

        appHasSettings: (state, getters) => (appName) => {
            let app = getters.findAppByName(appName);

            return app && Boolean(app.settings_url || false);
        },

        isApp: (state, getters) => (appName) => {
            let app = getters.findAppByName(appName);

            return app && Boolean(app.is_app || false);
        },

        appSettingsUrl: (state, getters) => (appName) => {
            let app = getters.findAppByName(appName);

            return app ? app.settings_url : null;
        },

        appSettingsPath: (state, getters) => (appName) => {
            const url = getters.appSettingsUrl(appName);

            return url ? String(url).replace(Config.get('app.BASEURL'), '') : null;
        },

        allAvailableApps: (state) => {
            return state.apps.filter(app => app.available);
        },

        allEnabledApps: (state) => {
            return state.apps.filter(app => app.enabled);
        },

        allDisabledApps: (state) => {
            return state.apps.filter(app => !app.enabled);
        },

        /**
         * Check if either Medipay or Convene is active and return the active one
         * Returns the app name if active, null if neither is active
         *
         * Usage examples:
         * - this.$store.getters['apps/activePaymentProvider']() // Returns 'medipay', 'convene', or null
         * - this.$store.getters['apps/activePaymentProvider']() === 'medipay' // Check if Medipay is active
         * - this.$store.getters['apps/activePaymentProvider']() !== null // Check if any payment provider is active
         */
        activePaymentProvider: (state, getters) => () => {
            // Check if Medipay is enabled
            if (getters.isAppEnabled('medipay')) {
                return 'medipay';
            }

            // Check if Convene is enabled (placeholder for future implementation)
            // TODO: Replace with actual Convene app check when implemented
            // if (getters.isAppEnabled('convene')) {
            //     return 'convene';
            // }

            // Return null if neither is active
            return null;
        },

        /**
         * Check if any payment provider (Medipay or Convene) is active
         * Returns true if any payment provider is enabled, false otherwise
         *
         * Usage examples:
         * - this.$store.getters['apps/hasActivePaymentProvider']() // Returns true/false
         */
        hasActivePaymentProvider: (state, getters) => () => {
            return getters.activePaymentProvider() !== null;
        },
    }
}
